import { CSC_IAM_REGISTERED_ROLE, FEATURE_TOGGLES } from '../Configuration'
import { getSavedInstrumentsCredentials } from '../FramedCheckout/cscPaymentsServices'
import { error } from '../Log'
import { handleApplePayMerchantValidation } from './handlers/handleApplePayMerchantValidation'
import { handlePaymentAuthorized } from './handlers/handlePaymentAuthorized'
import { handlePaymentMethodSelected } from './handlers/handlePaymentMethodSelected'
import { handleSessionCancelled } from './handlers/handleSessionCancelled'
import { handleShippingAddressSelected } from './handlers/handleShippingAddressSelected'
import { handleShippingMethodSelected } from './handlers/handleShippingMethodSelected'
import { transformOrderToApplePaySessionData } from './transformers/transformOrderToApplePaySessionData'

const isApplePayEnabled = () => (
  !!FEATURE_TOGGLES?.applePayEnabled
  || (new URLSearchParams(window.location.search).get('forceApplePayOn') === 'true')
)
const defaultHookWhenNotProvided = () => Promise.resolve()
export const startSession = ({
  logPrefix,
  initializationFunction = defaultHookWhenNotProvided,
  initialPaymentRequest,
  beforeAbort = defaultHookWhenNotProvided,
  beforeRedirectOnSuccess = defaultHookWhenNotProvided,
  orderParser = transformOrderToApplePaySessionData,
}: {
  showShipmentOptions?: boolean,
  logPrefix: string,
  initializationFunction?: () => Promise<unknown>,
  initialPaymentRequest: ApplePayJS.ApplePayPaymentRequest,
  beforeAbort?: () => Promise<void>
  beforeRedirectOnSuccess?: () => Promise<void>
  orderParser?: typeof transformOrderToApplePaySessionData
}) => {
  if (!isApplePayEnabled()) {
    throw new Error('Apple Pay is not enabled')
  }
  if (isApplePayEnabled()) {
    const prepNewSingleItemCheckoutPromise = initializationFunction()
    const session = new ApplePaySession(3, initialPaymentRequest)
    const onAbort = async () => {
      try {
        await beforeAbort()
      } catch (err) {
        error(`${logPrefix}: Failed to recreate cart after Apple Pay session abort`, err)
      }
      try {
        session?.abort?.()
      } catch (err) {
        error(`${logPrefix}: Failed to abort PDP Apple Pay session`, err)
      }
    }
    getSavedInstrumentsCredentials()
      .then((credentials) => {
        session.onvalidatemerchant = handleApplePayMerchantValidation({
          orderParser,
          session,
          credentials,
          onAbort,
        })
        session.onshippingcontactselected = handleShippingAddressSelected({
          orderParser,
          session,
          credentials,
          onAbort,
          beforeInitialize: async () => {
            try {
              await prepNewSingleItemCheckoutPromise
            } catch (err) {
              error('Failed to prepare apple pay data', err)
              await onAbort()
            }
          },
        })
        session.onshippingmethodselected = (
          handleShippingMethodSelected({
            orderParser,
            session,
            credentials,
            onAbort,
          })
        )
        session.onpaymentmethodselected = (
          handlePaymentMethodSelected({
            session,
            credentials,
            orderParser,
            onAbort,
          })
        )
        session.onpaymentauthorized = handlePaymentAuthorized({
          orderParser,
          onAbort,
          session,
          beforeFinalize: beforeRedirectOnSuccess,
          credentials,
          userType: (
            credentials.identityKey === CSC_IAM_REGISTERED_ROLE
              ? 'registered'
              : 'guest'
          ),
        })
        session.oncancel = handleSessionCancelled({
          orderParser,
          session,
          credentials,
          onAbort,
          beforeFinalize: beforeAbort,
        })
        session.begin()
      })
      .catch((err: unknown) => {
        error(`${logPrefix}: Session failed`, err)
        session.abort()
      })
  }
}

export default startSession
